import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    filterOrders();
  }, [statusFilter, searchTerm, orders]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/admin/orders");
      const ordersData = Array.isArray(response.data) ? response.data : [];
      setOrders(ordersData);
    } catch (err) {
      console.error("❌ Error fetching orders:", err);
      setError(`Không thể tải danh sách đơn hàng: ${err.message}`);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const filterOrders = () => {
    let filtered = [...orders];
    if (statusFilter !== "all") {
      filtered = filtered.filter(order => order.status === statusFilter);
    }
    if (searchTerm) {
      filtered = filtered.filter(order => {
        const orderId = order._id || "";
        const customerName = order.customerName || "";
        const customerEmail = order.customerEmail || "";
        return (
          orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customerEmail.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }
    setFilteredOrders(filtered);
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    const orderToUpdate = orders.find(o => o._id === orderId);
    if (orderToUpdate && orderToUpdate.status === "Đã hủy") {
      alert("Không thể thay đổi trạng thái của đơn hàng đã bị hủy!");
      return;
    }

    try {
      await api.put(`/orders/${orderId}`, { status: newStatus });
      setOrders(orders.map(order =>
        order._id === orderId ? { ...order, status: newStatus } : order
      ));
      if (selectedOrder && selectedOrder._id === orderId) {
        setSelectedOrder({ ...selectedOrder, status: newStatus });
      }
      alert("Cập nhật trạng thái thành công!");
    } catch (err) {
      console.error("Error updating order status:", err);
      alert("Không thể cập nhật trạng thái đơn hàng");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN").format(amount) + "₫";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Đã giao": return "bg-green-50 text-green-600 border-green-200";
      case "Đang giao": return "bg-blue-50 text-blue-600 border-blue-200";
      case "Đã xác nhận": return "bg-indigo-50 text-indigo-600 border-indigo-200";
      case "Chờ xác nhận":
      case "Chờ thanh toán": return "bg-yellow-50 text-yellow-600 border-yellow-200";
      case "Đã hủy": return "bg-red-50 text-red-600 border-red-200";
      default: return "bg-gray-50 text-gray-600 border-gray-200";
    }
  };

  const viewOrderDetail = (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 lg:p-8 bg-gray-50 min-h-screen font-inter">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-3xl font-black text-slate-800 tracking-tight">Đơn hàng</h1>
          <p className="text-[10px] sm:text-sm text-slate-400 font-bold uppercase tracking-widest mt-1">Hệ thống ghi nhận: {orders.length} đơn</p>
        </div>
        <Link to="/admin" className="text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-xl text-xs font-black transition-all border border-blue-100 bg-white shadow-sm inline-flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px]">dashboard</span>
          Bảng điều khiển
        </Link>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mb-6 border border-gray-100">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-[20px]">search</span>
            <input
              type="text"
              placeholder="Mã đơn, tên, email..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-bold transition-all placeholder:text-slate-300"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar -mx-1 px-1">
            {["all", "Chờ xác nhận", "Đã xác nhận", "Đang giao", "Đã giao", "Đã hủy"].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-xl text-[10px] sm:text-xs font-black whitespace-nowrap transition-all uppercase tracking-wider ${
                  statusFilter === status
                    ? "bg-slate-800 text-white shadow-lg shadow-slate-200"
                    : "bg-gray-100 text-slate-400 hover:bg-gray-200"
                }`}
              >
                {status === "all" ? "Tất cả" : (status === "Chờ thanh toán" ? "Chờ XN" : status)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full">
            <thead className="bg-gray-50/50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-4 text-left text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">Đơn hàng</th>
                <th className="px-4 py-4 text-left text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest hidden sm:table-cell">Khách hàng</th>
                <th className="px-4 py-4 text-left text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest hidden md:table-cell">Sản phẩm</th>
                <th className="px-4 py-4 text-left text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">Tổng tiền</th>
                <th className="px-4 py-4 text-left text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">Trạng thái</th>
                <th className="px-4 py-4 text-right text-[10px] sm:text-xs font-black text-slate-400 uppercase tracking-widest">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredOrders.length === 0 ? (
                <tr><td colSpan="6" className="py-20 text-center text-slate-300 font-bold uppercase text-[10px] tracking-widest">Không có đơn hàng</td></tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={order._id} className="hover:bg-blue-50/10 transition-colors group">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button onClick={() => viewOrderDetail(order)} className="text-xs sm:text-sm font-black text-blue-600 hover:underline cursor-pointer focus:outline-none transition-all text-left">
                        #{order._id?.toString().slice(-6).toUpperCase()}
                      </button>
                      <p className="text-[10px] font-bold text-slate-400 block sm:hidden truncate max-w-[100px] mt-0.5">{order.customerName}</p>
                      <div className="block md:hidden mt-2 max-w-[200px]">
                        {order.items?.filter(item => !item.isGift && !(item.name && item.name.toLowerCase().includes("quà tặng"))).map((item, idx) => (
                           <div key={idx} className="flex items-center gap-2 mb-1.5 last:mb-0">
                             <a href={`/product/${item.productId || item.product}`} target="_blank" rel="noopener noreferrer" className="shrink-0">
                               <img src={item.image} alt={item.name} className="w-8 h-8 object-cover rounded bg-gray-50 border border-gray-100" />
                             </a>
                             <div className="flex flex-col min-w-0">
                               <a href={`/product/${item.productId || item.product}`} target="_blank" rel="noopener noreferrer" className="text-[9px] text-slate-700 font-bold truncate hover:text-blue-600">
                                 {item.name}
                               </a>
                               <span className="text-[8px] text-slate-500">S:{item.selectedSize} | x{item.qty || item.quantity}</span>
                             </div>
                           </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap hidden sm:table-cell">
                      <div className="min-w-0">
                        <p className="text-xs font-black text-slate-800 truncate">{order.customerName}</p>
                        <p className="text-[10px] font-bold text-slate-400 truncate">{order.customerPhone}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <div className="flex flex-col gap-2 max-w-[250px]">
                        {order.items?.filter(item => !item.isGift && !(item.name && item.name.toLowerCase().includes("quà tặng"))).map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2 border-b border-gray-50 pb-2 mb-2 last:border-0 last:pb-0 last:mb-0">
                             <a href={`/product/${item.productId || item.product}`} target="_blank" rel="noopener noreferrer" className="shrink-0">
                               <img src={item.image} alt={item.name} className="w-10 h-10 object-cover rounded-lg border border-gray-100 hover:scale-105 transition-transform" />
                             </a>
                             <div className="flex flex-col min-w-0">
                               <a href={`/product/${item.productId || item.product}`} target="_blank" rel="noopener noreferrer" className="text-[11px] font-black text-slate-800 truncate hover:text-blue-600 hover:underline transition-colors" title={item.name}>
                                 {item.name}
                               </a>
                               <span className="text-[9px] font-bold text-slate-500 mt-0.5">Size: {item.selectedSize} | Màu: {item.selectedColor} | SL: {item.qty || item.quantity}</span>
                             </div>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <p className="text-xs sm:text-sm font-black text-slate-900">{formatCurrency(order.total)}</p>
                      <p className="text-[9px] font-bold text-slate-400 mt-0.5">{order.paymentMethod}</p>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-0.5 text-[9px] sm:text-[10px] font-black rounded-lg border uppercase tracking-wider ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1 sm:gap-2">
                        <button
                          onClick={() => viewOrderDetail(order)}
                          className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                          title="Chỉnh sửa đơn hàng"
                        >
                          <span className="material-symbols-outlined text-[18px] sm:text-[20px]">edit</span>
                        </button>
                        {order.status !== "Đã hủy" && order.status !== "Đã giao" && (
                          <button
                            onClick={() => {
                              if (window.confirm(`Bạn có chắc chắn muốn hủy đơn hàng #${order._id?.toString().slice(-6).toUpperCase()} không?`)) {
                                updateOrderStatus(order._id, "Đã hủy");
                              }
                            }}
                            className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-xl transition-all"
                            title="Hủy đơn hàng"
                          >
                            <span className="material-symbols-outlined text-[18px] sm:text-[20px]">cancel</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal - Mobile Drawer Style */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 z-[100] flex sm:items-center justify-center p-0 sm:p-4 bg-slate-900/60 backdrop-blur-sm overflow-hidden">
          <div className="bg-white w-full max-w-2xl h-full sm:h-auto sm:max-h-[90vh] sm:rounded-[32px] overflow-hidden shadow-2xl animate-modalUp flex flex-col">
            <div className="p-4 sm:p-6 bg-slate-900 text-white flex justify-between items-center shrink-0">
              <h3 className="text-sm sm:text-lg font-black uppercase tracking-widest flex items-center gap-3">
                <span className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                  <span className="material-symbols-outlined text-white">receipt_long</span>
                </span>
                Chi tiết đơn #{selectedOrder._id?.toString().slice(-6).toUpperCase()}
              </h3>
              <button onClick={() => setShowDetailModal(false)} className="w-10 h-10 rounded-full hover:bg-slate-800 flex items-center justify-center bg-slate-800">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 sm:p-8 no-scrollbar bg-white">
              <div className="space-y-8">
                {/* Status Stepper */}
                <div className="bg-slate-50 rounded-2xl p-4 sm:p-6">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">Trạng thái hiện tại</h4>
                  <div className="flex flex-wrap gap-2">
                    {["Chờ xác nhận", "Đã xác nhận", "Đang giao", "Đã giao", "Đã hủy"].map(status => (
                      <button
                        key={status}
                        onClick={() => updateOrderStatus(selectedOrder._id, status)}
                        disabled={selectedOrder.status === "Đã hủy" && status !== "Đã hủy"}
                        className={`px-3 py-1.5 rounded-xl text-[10px] font-black transition-all border uppercase tracking-wider ${
                          selectedOrder.status === status
                            ? getStatusColor(status) + " ring-2 ring-offset-2 ring-current"
                            : "bg-white text-slate-400 border-slate-100 hover:border-slate-200"
                        } ${selectedOrder.status === "Đã hủy" && status !== "Đã hủy" ? "opacity-50 cursor-not-allowed grayscale" : ""}`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-gray-100 pb-2">Người nhận</h4>
                    <div className="space-y-1">
                      <p className="text-sm font-black text-slate-800">{selectedOrder.customerName}</p>
                      <p className="text-xs font-bold text-slate-500">{selectedOrder.customerPhone} • {selectedOrder.customerEmail}</p>
                      <p className="text-xs font-bold text-slate-400 leading-relaxed mt-2">{selectedOrder.address}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-gray-100 pb-2">Thanh toán</h4>
                    <div className="space-y-1">
                      <p className="text-sm font-black text-slate-800">{selectedOrder.paymentMethod}</p>
                      <p className="text-xs font-bold text-slate-400 mt-2">Ngày đặt: {formatDate(selectedOrder.createdAt)}</p>
                    </div>
                  </div>
                </div>

                {/* Items */}
                <div className="space-y-4">
                  <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-gray-100 pb-2">Danh sách sản phẩm</h4>
                  <div className="space-y-3">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex gap-3 bg-gray-50/50 p-2 sm:p-3 rounded-2xl border border-gray-50">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 relative flex-shrink-0">
                          {(!item.isGift && !(item.name && item.name.toLowerCase().includes("quà tặng"))) ? (
                            <a 
                              href={`/product/${item.productId || item.product}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="block w-full h-full hover:scale-105 transition-transform"
                            >
                              <img
                                src={item.image}
                                alt=""
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "https://cdn-icons-png.flaticon.com/512/679/679821.png"; // Gift icon fallback
                                }}
                                className="w-full h-full rounded-xl object-cover border border-white shadow-sm"
                              />
                            </a>
                          ) : (
                            <img
                              src={item.image}
                              alt=""
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://cdn-icons-png.flaticon.com/512/679/679821.png"; // Gift icon fallback
                              }}
                              className="w-full h-full rounded-xl object-cover border border-white shadow-sm"
                            />
                          )}
                          {(item.isGift || (item.name && item.name.toLowerCase().includes("quà tặng"))) && (
                            <div className="absolute -top-1 -right-1 bg-blue-600 text-white rounded-full p-0.5 shadow-sm">
                              <span className="material-symbols-outlined text-[10px]">redeem</span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          {(!item.isGift && !(item.name && item.name.toLowerCase().includes("quà tặng"))) ? (
                            <a 
                              href={`/product/${item.productId || item.product}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs sm:text-sm font-black text-slate-800 truncate hover:text-blue-600 hover:underline transition-colors block"
                            >
                              {item.name}
                            </a>
                          ) : (
                            <p className="text-xs sm:text-sm font-black text-slate-800 truncate">{item.name}</p>
                          )}
                          <p className="text-[10px] font-bold text-slate-400">
                            Size: {item.selectedSize} • Màu: {item.selectedColor}
                            {item.isSecondHand && (
                              <span className="ml-2 text-[8px] bg-amber-500 text-white px-1 py-0.5 rounded font-black uppercase tracking-tighter inline-flex items-center gap-0.5">
                                <span className="material-symbols-outlined text-[10px]">recycling</span>
                                SecondHand
                              </span>
                            )}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-[10px] font-black text-slate-600">x{item.qty || item.quantity}</span>
                            <span className="text-xs sm:text-sm font-black text-slate-900">{formatCurrency(item.price)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total */}
                <div className="bg-slate-900 text-white rounded-[24px] p-6 shadow-xl shadow-slate-200">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-black uppercase tracking-widest text-slate-400">Tổng thanh toán</span>
                    <span className="text-2xl font-black">{formatCurrency(selectedOrder.total)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 bg-gray-50 border-t border-gray-100 shrink-0">
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-full py-3.5 sm:py-4 bg-white border border-slate-200 text-slate-800 rounded-2xl font-black text-xs sm:text-sm uppercase tracking-widest hover:bg-slate-50 transition-all shadow-sm"
              >
                Đóng cửa sổ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;